import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete, Box, Chip, CircularProgress, Stack, TextField, Typography,
} from '@mui/material';
import { api } from '../api/client';
import type { ExternalPlace, Poi } from '../api/types';

/**
 * A local place, or an external search result shown alongside it.
 *
 * External results have no database id, so choosing one produces a pinned
 * coordinate rather than a stored place — which is also why they are never
 * written back to the database.
 */
export type PlaceOption =
  | { kind: 'local'; poi: Poi }
  | { kind: 'external'; place: ExternalPlace };

export const optionLabel = (o: PlaceOption) =>
  o.kind === 'local' ? o.poi.name : o.place.name;
export const optionKey = (o: PlaceOption) =>
  o.kind === 'local' ? o.poi.id : `ext:${o.place.id}`;

interface BaseProps {
  cityId: string;
  /**
   * Search every city rather than the one selected above.
   *
   * Routing crosses city lines freely, so a round can start in one and end
   * in another — and the form was the only thing insisting otherwise. The
   * city still matters: it biases the provider's own search, and it is what
   * the map is looking at.
   */
  allCities?: boolean;
  label: string;
  placeholder?: string;
  /** Restrict to places that can be itinerary stops. */
  visitableOnly?: boolean;
  /** Restrict to places the traveller is allowed to avoid. */
  avoidableOnly?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** Narrows the search to these categories, so a name is easier to find. */
  categoryKeys?: string[];
  /**
   * Called when an external result is chosen. Those carry no database id, so
   * the caller turns one into a pinned coordinate rather than a stored place.
   * Omit to search local places only.
   */
  onExternalPick?: (place: ExternalPlace) => void;
}

/**
 * Reads a typed coordinate pair, or null if the text is not one.
 *
 * Accepts what people actually paste: a comma or spaces between the numbers,
 * optional sign, optional surrounding brackets. Latitude first, as every
 * mapping tool writes it.
 */
export function parseLatLng(text: string): { lat: number; lng: number } | null {
  const match = text
    .trim()
    .replace(/^[([]|[)\]]$/g, '')
    .match(/^(-?\d{1,3}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!match) return null;

  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** A typed coordinate, shaped like a search result so it can be chosen. */
function coordinateOption(point: { lat: number; lng: number }): PlaceOption {
  return {
    kind: 'external',
    place: {
      id: `coords:${point.lat},${point.lng}`,
      name: `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`,
      address: 'Use these coordinates',
      lat: point.lat,
      lng: point.lng,
      source: 'coords',
    },
  };
}

/**
 * The shortest query worth sending outside.
 *
 * Every external search is a billable request, and one or two letters describes
 * nothing — the provider would be paid to guess. Below this, and whenever the
 * box is empty, only the local database is searched.
 */
const MIN_EXTERNAL_QUERY_LENGTH = 3;

/**
 * How many places a page of search results holds.
 *
 * It was a flat cap: the first twenty-five matches and no way to the rest, so
 * a short query stopped somewhere in the Cs and the list looked like the whole
 * answer. The same number is now a page, fetched again as the list scrolls.
 */
const SEARCH_PAGE = 25;

/**
 * Asks for the next page as the open list nears its end.
 *
 * MUI's listbox is an ordinary scrolling element, so this is its scroll
 * handler rather than anything observed. The threshold is most of a row's
 * height: fetching exactly at the bottom makes the list stop dead for as long
 * as the request takes, and starting a page early hides that behind the
 * scrolling already under way.
 */
function nearBottom(event: React.UIEvent<HTMLElement>): boolean {
  const el = event.currentTarget;

  return el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
}

/** Debounced POI lookup shared by both the single and multiple variants. */
function usePoiSearch(
  cityId: string,
  visitableOnly: boolean | undefined,
  query: string,
  categoryKeys?: string[],
  allCities?: boolean,
) {
  const [options, setOptions] = useState<Poi[]>([]);
  const [externalOptions, setExternalOptions] = useState<ExternalPlace[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  /*
   * What the next page would be asked WITH.
   *
   * Held rather than rebuilt by `loadMore`, so the page that arrives belongs
   * to the query that is on screen: typing again replaces this, and a
   * scroll-triggered fetch for the old text cannot append to the new list.
   */
  const pageArgs = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!cityId) return;
    let cancelled = false;
    setLoading(true);
    // Drop the previous query's results immediately. Leaving them on screen
    // during the debounce lets a fast click select a place that does not
    // match what was typed.
    setOptions([]);
    setExternalOptions([]);

    // A short debounce keeps typing responsive without hammering the API.
    const timer = setTimeout(async () => {
      try {
        /*
         * External search runs for any field, but only on a real query.
         *
         * It used to be blocked wherever `visitableOnly` was set, which ruled
         * it out of the place-to-visit and break fields — exactly the ones
         * where a name the local data does not carry is most likely. A chosen
         * result becomes a pinned coordinate, and a coordinate is always
         * visitable, so the restriction bought nothing.
         */
        const typed = query.trim();
        const external = typed.length >= MIN_EXTERNAL_QUERY_LENGTH
          ? api.searchExternal(cityId, typed).then((r) => r.results).catch(() => [])
          : Promise.resolve([]);

        const args = {
          /*
           * Widened only once something has been typed.
           *
           * Omitting the city is what widens it — the endpoint reads that as
           * "every city", each row judged by its own city's dataset. With an
           * empty box there is nothing to widen TO: the list is then just the
           * most prominent places anywhere, which is a worse answer than the
           * most prominent places here. So the city stands until the operator
           * says what they are looking for.
           */
          ...(allCities && typed ? {} : { cityId }),
          q: query || undefined,
          ...(visitableOnly ? { visitableOnly: true } : {}),
          ...(categoryKeys?.length ? { categoryKeys } : {}),
          limit: SEARCH_PAGE,
        };

        pageArgs.current = args;

        const { pois: results, hasMore: more } = await api.searchPois(args);
        const extra = await external;
        if (cancelled) return;
        // Local places first: they are what routing actually knows about.
        setOptions(results);
        setHasMore(more);
        setExternalOptions(
          extra.filter((e) => !results.some((p) => p.name.toLowerCase() === e.name.toLowerCase())),
        );
      } catch {
        if (!cancelled) {
          setOptions([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // categoryKeys is joined so a new array of the same values does not refetch
  }, [cityId, allCities, query, visitableOnly, categoryKeys?.join(',')]);

  /*
   * The next page, appended.
   *
   * Guarded on its own flag rather than on `loading`: the first page's
   * spinner belongs to the field, and a scroll that arrives while it is still
   * running must do nothing rather than fetch page two of a list that has no
   * page one yet.
   */
  const loadMore = useCallback(() => {
    const args = pageArgs.current;
    if (!args || loading || loadingMore || !hasMore) return;

    setLoadingMore(true);
    void (async () => {
      try {
        const page = await api.searchPois({ ...args, offset: options.length });

        // Appended by id, because a place already on screen arriving again
        // would give two rows the same key and React the wrong one to update.
        setOptions((current) => {
          const seen = new Set(current.map((p) => p.id));

          return [...current, ...page.pois.filter((p) => !seen.has(p.id))];
        });
        setHasMore(page.hasMore);
      } catch {
        // Stop asking rather than retry on every scroll event.
        setHasMore(false);
      } finally {
        setLoadingMore(false);
      }
    })();
  }, [loading, loadingMore, hasMore, options.length]);

  return { options, externalOptions, loading, hasMore, loadingMore, loadMore };
}

function renderPoiOption(
  props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
  option: PlaceOption,
) {
  const { key, ...rest } = props;
  /*
   * The city, first.
   *
   * A search that crosses city lines returns four Union Stations, and the
   * address alone does not separate them at a glance — the city does.
   */
  const detail =
    option.kind === 'local'
      ? [option.poi.cityName, option.poi.categoryLabel, option.poi.address]
        .filter(Boolean).join(' · ')
      : option.place.address ?? '';

  /*
   * The whole line, on hover.
   *
   * Both lines are `noWrap`, because a list that reflows as you type is
   * unreadable — but that truncates exactly where it matters: "2200 C Street
   * Northw…" is not enough to tell two branches apart. The browser's own
   * tooltip carries the rest, at no layout cost.
   */
  const title = [optionLabel(option), detail].filter(Boolean).join(' — ');

  return (
    <Box component="li" key={key} title={title} {...rest} sx={{ py: 0.5 }}>
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" noWrap sx={{ lineHeight: 1.35, flex: 1 }}>
            {optionLabel(option)}
          </Typography>
          {/* Marked, so it is obvious which results the router actually knows. */}
          {option.kind === 'external' && (
            <Chip
              size="small"
              variant="outlined"
              color={option.place.source === 'coords' ? 'primary' : 'default'}
              label={option.place.source === 'coords' ? 'lat, lng' : 'web'}
              sx={{ height: 17, fontSize: 10 }}
            />
          )}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: 'block', lineHeight: 1.3 }}
        >
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}

// ── single select ───────────────────────────────────────────────────

interface SingleProps extends BaseProps {
  value: Poi | null;
  onChange: (poi: Poi | null) => void;
}

export function PlaceAutocomplete({
  cityId, allCities, label, placeholder, visitableOnly, helperText, disabled, value,
  onChange, categoryKeys,
  onExternalPick,
}: SingleProps) {
  const [query, setQuery] = useState(value?.name ?? '');

  /*
   * Keeps the visible text in step with the selection.
   *
   * `inputValue` is controlled here, and the reset MUI fires to show a
   * selected option is deliberately ignored below so that typing is not
   * wiped mid-search. That leaves one gap: mounting with a value already
   * chosen shows an empty box. Syncing on the value itself closes it —
   * typing changes `query` alone, so this only fires on a real selection.
   */
  useEffect(() => {
    setQuery(value?.name ?? '');
  }, [value]);
  const { options, externalOptions, loading, loadingMore, loadMore } = usePoiSearch(
    cityId, visitableOnly, query, categoryKeys, allCities,
  );

  // A typed coordinate leads: it is exact, and nothing found by name beats it.
  const typedPoint = parseLatLng(query);
  const merged: PlaceOption[] = [
    ...(typedPoint && onExternalPick ? [coordinateOption(typedPoint)] : []),
    ...options.map((poi) => ({ kind: 'local' as const, poi })),
    ...(onExternalPick
      ? externalOptions.map((place) => ({ kind: 'external' as const, place }))
      : []),
  ];

  return (
    <Autocomplete
      value={value ? { kind: 'local' as const, poi: value } : null}
      onChange={(_, next) => {
        if (!next) return onChange(null);
        if (next.kind === 'local') return onChange(next.poi);
        // No database id, so it becomes a pinned coordinate instead.
        onExternalPick?.(next.place);
      }}
      inputValue={query}
      onInputChange={(_, next, reason) => reason !== 'reset' && setQuery(next)}
      options={merged}
      loading={loading || loadingMore}
      disabled={disabled}
      getOptionLabel={optionLabel}
      getOptionKey={optionKey}
      isOptionEqualToValue={(a, b) => optionKey(a) === optionKey(b)}
      filterOptions={(x) => x} // server already filtered
      slotProps={{
        listbox: {
          sx: { maxHeight: 260 },
          // The list asks for its own next page as it nears the end.
          onScroll: (event: React.UIEvent<HTMLElement>) => {
            if (nearBottom(event)) loadMore();
          },
        },
        popper: { placement: 'bottom-start', modifiers: [{ name: 'flip', enabled: false }] },
      }}
      renderOption={renderPoiOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          /*
            A label longer than its field is trimmed, not spilled.

            The outlined label sits on the notch and MUI lets it run past the
            border: at the width these pickers get inside a form column,
            "Add a single place to avoid" crossed its own outline and ran
            under the Add button. Room is left for the dropdown arrow.
          */
          sx={{
            '& .MuiInputLabel-root': {
              maxWidth: 'calc(100% - 3rem)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          slotProps={{
            // Spread the whole object: `htmlInput` carries the ref Autocomplete
            // uses to find the real <input>, and replacing slotProps wholesale
            // drops it.
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading || loadingMore ? <CircularProgress size={16} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

// ── multiple select ─────────────────────────────────────────────────

interface MultiProps extends BaseProps {
  value: Poi[];
  onChange: (pois: Poi[]) => void;
  max?: number;
  /**
   * Keeps the picked places out of the box itself.
   *
   * For the lists that show what has been chosen underneath, each row with a
   * stay time and a way to remove it. Chips in the box as well would say the
   * same thing twice, and the box grows a line per place until the search
   * field somebody is typing into has been pushed off its own control.
   */
  hideChips?: boolean;
}

export function PlaceMultiAutocomplete({
  cityId, allCities, label, placeholder, visitableOnly, avoidableOnly, helperText, disabled,
  value, onChange, max = 20, categoryKeys, onExternalPick, hideChips,
}: MultiProps) {
  const [query, setQuery] = useState('');
  const { options, externalOptions, loading, loadingMore, loadMore } = usePoiSearch(
    cityId, visitableOnly, query, categoryKeys, allCities,
  );

  const typedPoint = parseLatLng(query);
  const selectable = useMemo<PlaceOption[]>(
    () => [
      ...(typedPoint && onExternalPick ? [coordinateOption(typedPoint)] : []),
      ...options
        .filter((option) => !value.some((v) => v.id === option.id))
        .map((poi) => ({ kind: 'local' as const, poi })),
      ...(onExternalPick
        ? externalOptions.map((place) => ({ kind: 'external' as const, place }))
        : []),
    ],
    [options, externalOptions, value, onExternalPick, typedPoint?.lat, typedPoint?.lng],
  );

  const atLimit = value.length >= max;

  return (
    <Autocomplete
      multiple
      value={value.map((poi) => ({ kind: 'local' as const, poi }))}
      onChange={(_, next) => {
        const external = next.find((o) => o.kind === 'external');
        if (external?.kind === 'external') onExternalPick?.(external.place);
        onChange(
          next
            .filter((o): o is { kind: 'local'; poi: Poi } => o.kind === 'local')
            .map((o) => o.poi)
            .slice(0, max),
        );
      }}
      inputValue={query}
      onInputChange={(_, next, reason) => reason !== 'reset' && setQuery(next)}
      options={selectable}
      loading={loading || loadingMore}
      disabled={disabled}
      getOptionLabel={optionLabel}
      getOptionKey={optionKey}
      isOptionEqualToValue={(a, b) => optionKey(a) === optionKey(b)}
      filterOptions={(x) => x}
      slotProps={{
        listbox: {
          sx: { maxHeight: 260 },
          // The list asks for its own next page as it nears the end.
          onScroll: (event: React.UIEvent<HTMLElement>) => {
            if (nearBottom(event)) loadMore();
          },
        },
        popper: { placement: 'bottom-start', modifiers: [{ name: 'flip', enabled: false }] },
      }}
      renderOption={renderPoiOption}
      renderValue={(selected, getItemProps) =>
        (hideChips ? [] : selected).map((option, index) => {
          const { key, ...chipProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              {...chipProps}
              size="small"
              label={optionLabel(option)}
              color={avoidableOnly ? 'warning' : 'default'}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={atLimit ? undefined : placeholder}
          helperText={atLimit ? `Maximum ${max} selected` : helperText}
          /*
            A label longer than its field is trimmed, not spilled.

            The outlined label sits on the notch and MUI lets it run past the
            border: at the width these pickers get inside a form column,
            "Add a single place to avoid" crossed its own outline and ran
            under the Add button. Room is left for the dropdown arrow.
          */
          sx={{
            '& .MuiInputLabel-root': {
              maxWidth: 'calc(100% - 3rem)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          slotProps={{
            // Spread the whole object: `htmlInput` carries the ref Autocomplete
            // uses to find the real <input>, and replacing slotProps wholesale
            // drops it.
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading || loadingMore ? <CircularProgress size={16} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
